import Fuse from 'fuse.js'
import {
  buildLocalRegistryMiniSearch,
  searchLocalRegistryMiniSearch,
} from './buildLocalRegistryMiniSearch'

// Curated static quick-actions. Each entry exposes a logical `action` token
// that the UI maps to a side-effect (creating a new set/question/repo/section).
// Adding a new quick action is a single entry here plus a matching arm in
// `onSuggestionSelected` inside the top-nav search bar.
//
// Hits are rendered as Algolia-shaped objects with __quickAction: true and
// __localOrigin: true. They surface in two places:
//   1. The dropdown empty-state (focused, no query) — full list, no fuzz.
//   2. Mid-query results — fuzzy-matched against `label` + `keywords`.

// `prefill: true` marks entries shown in the dropdown empty-state (focused, no
// query). The empty-state list is built by filtering on this flag, so adding a
// future prefill entry is a one-line change here (and in the nav/external
// registries) with no consumer changes.
export const STATIC_QUICK_ACTIONS = [
  {
    id: 'qa-new-set',
    label: 'New Set',
    icon: 'compose',
    action: 'newSet',
    keywords: ['create', 'add', 'set', 'new'],
    prefill: true,
  },
  {
    id: 'qa-new-pack',
    label: 'New Pack',
    icon: 'add-pack',
    action: 'newRepo',
    keywords: ['create', 'add', 'pack', 'repo', 'new'],
    prefill: true,
  },
  {
    id: 'qa-new-class',
    label: 'New Class',
    icon: 'add-class',
    action: 'newSection',
    keywords: ['create', 'add', 'class', 'section', 'new'],
    prefill: true,
  },
  {
    id: 'qa-upgrade-pro',
    label: 'Upgrade to Pro',
    icon: 'gear',
    action: 'upgradePro',
    keywords: ['pro', 'upgrade', 'billing', 'subscription', 'plan', 'premium', 'paid'],
    prefill: false,
  },
]

// Exported so the internal search-tuning sandbox can display/reset the defaults.
export const QUICK_ACTIONS_FUSE_OPTIONS = {
  threshold: 0.2,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
  includeMatches: true,
  keys: [
    { name: 'label', weight: 2 },
    { name: 'keywords', weight: 1 },
  ],
}

/**
 * Build a Fuse index over the quick-actions list. Caller is expected to
 * memoise the entries reference; this function is a thin allocator.
 */
export function buildQuickActionsFuseIndex(entries = STATIC_QUICK_ACTIONS) {
  return new Fuse(Array.isArray(entries) ? entries : [], QUICK_ACTIONS_FUSE_OPTIONS)
}

/**
 * Project a raw entry into the Algolia-shaped hit consumed by the dropdown.
 * Used for the empty-state list where no Fuse query is run.
 */
export function toQuickActionHit(entry) {
  return {
    ...entry,
    objectID: entry.id,
    __quickAction: true,
    __localOrigin: true,
  }
}

/**
 * Run a query against the Fuse quick-actions index and return Algolia-shaped
 * flat hits, each carrying __quickAction: true, __localOrigin: true.
 */
export function searchQuickActions(fuse, query, limit = 6) {
  if (!query || !fuse) return []
  return fuse.search(query, { limit }).map(({ item, score, matches }) => ({
    ...toQuickActionHit(item),
    __fuseScore: score,
    __fuseMatches: matches,
  }))
}

/**
 * Build a MiniSearch index over the quick-actions list — the BM25 counterpart
 * of buildQuickActionsFuseIndex.
 */
export function buildQuickActionsMiniSearchIndex(entries = STATIC_QUICK_ACTIONS) {
  return buildLocalRegistryMiniSearch(entries)
}

/**
 * Run a query against the MiniSearch quick-actions index and return
 * Algolia-shaped hits mirroring searchQuickActions, tagged with
 * __localEngine: 'minisearch' and __minisearchScore.
 */
export function searchQuickActionsMiniSearch(ms, query, limit = 6) {
  if (!ms || !query) return []
  const byId = new Map(STATIC_QUICK_ACTIONS.map((e) => [e.id, e]))
  return searchLocalRegistryMiniSearch(ms, query, undefined, limit).map((result) => {
    const entry = byId.get(result.id) || { id: result.id, label: result.label }
    return {
      ...toQuickActionHit(entry),
      __localEngine: 'minisearch',
      __minisearchScore: result.score,
      __minisearchTerms: result.matchedTerms,
    }
  })
}
